import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { API_URL } from 'constant';
import { useNetwork } from 'hooks';
import { getOffDate } from 'utils';

/**
 * The saved filter tags, in the shape /complex-sessions wants.
 *
 * Step filters go up as one `includeSteps` JSON array — accreditation tiers and
 * fund direction are the same step with a `subType`, which is why 506b/506c and
 * payIn/payOut are folded rather than sent as their own stepIds. Funds, flows
 * and assignees are repeated `name[]` params.
 */
export const parseFilterTags = (data: Record<string, string[]> = {}) => {
	const statuses = ['pending', 'processing', 'completed', 'rejected'];
	const folded = ['506b', '506c', 'payIn', 'payOut'];
	const actions: { stepId: string; status: string[]; subType?: string }[] = [];
	let funds: string[] = [];
	let flows: string[] = [];
	let assignees: string[] = [];

	Object.keys(data).forEach(key => {
		if (data[key]?.some(value => statuses.includes(value))) {
			actions.push(
				folded.includes(key)
					? {
							stepId: key === '506b' || key === '506c' ? 'accreditation' : 'fundInvestment',
							status: data[key] ?? [],
							subType: key,
					  }
					: { stepId: key, status: data[key] ?? [] }
			);
		}
		if (key === 'assignedUserIds') assignees = data[key] ?? [];
		if (key === 'fund_name') funds = data[key] ?? [];
		if (key === 'onboardingFlowName') flows = data[key] ?? [];
	});

	let query = actions.length ? `includeSteps=${JSON.stringify(actions)}&` : '';
	funds.forEach(n => (query += `fundName[]=${encodeURIComponent(n)}&`));
	assignees.forEach(id => (query += `assignedUserIds[]=${encodeURIComponent(id)}&`));
	flows.forEach(n => (query += `pipelineName[]=${encodeURIComponent(n)}&`));
	return query.endsWith('&') ? query.slice(0, -1) : query;
};

export type ITabKey =
	| 'all'
	| 'pending'
	| 'processing'
	| 'rejected'
	| 'completed'
	| 'amlWarning'
	| 'assigned';

export interface ICounts {
	total: number;
	all: number;
	pending: number;
	processing: number;
	rejected: number;
	completed: number;
	amlWarning: number;
	assigned: number;
}

const NO_COUNTS: ICounts = {
	total: 0,
	all: 0,
	pending: 0,
	processing: 0,
	rejected: 0,
	completed: 0,
	amlWarning: 0,
	assigned: 0,
};

export const PAGE_SIZE = 15;

export interface IDeckQuery {
	tab: ITabKey;
	search: string;
	sortBy: 'createdAt' | 'updatedAt';
	archived: boolean;
	page: number;
	/** the date-range chip; both ends inclusive of their local day */
	startDate?: Date | string;
	endDate?: Date | string;
	/** saved filter tags (steps, funds, flows, assignees) */
	filters?: Record<string, string[]>;
}

/**
 * The sessions list, and nothing else.
 *
 * `pagination=true` is what makes /complex-sessions answer with a page plus the
 * per-tab unviewed counts the tab badges need — without it the endpoint returns
 * every session in one 3 MB response (there is no `limit`; passing one is a
 * 400). The counts come back on the same wrapper, so the badges cost no extra
 * request.
 *
 * A search or a tab click can outrun a slower earlier request, so each load
 * carries a generation and a late response is dropped rather than painted over
 * a newer one.
 */
export const useSessionsDeck = (query: IDeckQuery) => {
	// updateState:false — rows are read from the returned payload, so the
	// network hook's own data/status writes are pure re-render cost.
	const { get } = useNetwork({ updateState: false });

	const [rows, setRows] = useState<any[]>([]);
	const [counts, setCounts] = useState<ICounts>(NO_COUNTS);
	const [loaded, setLoaded] = useState(false);
	const [error, setError] = useState(false);

	const generation = useRef(0);
	const { tab, search, sortBy, archived, page, startDate, endDate, filters } = query;
	const filterQuery = useMemo(() => parseFilterTags(filters), [filters]);

	const url = useMemo(() => {
		const params = new URLSearchParams({
			isDeleted: String(archived),
			pageNo: String(search ? 1 : page),
			pagination: 'true',
			pageSize: String(PAGE_SIZE),
			isAssignedToMe: String(tab === 'assigned'),
			dateFilterBy: sortBy,
		});
		if (search) params.set('textSearch', search);
		if (startDate) params.set('startDate', getOffDate(startDate, 'start'));
		if (endDate) params.set('endDate', getOffDate(endDate, 'end'));
		let built = `${API_URL.COMPLEX_SESSION}?${params.toString()}`;
		// `assigned` is not a status — it is the same list filtered to me.
		if (tab !== 'all' && tab !== 'assigned') built += `&status[]=${tab}`;
		if (filterQuery) built += `&${filterQuery}`;
		return built;
	}, [archived, page, search, sortBy, tab, startDate, endDate, filterQuery]);

	const load = useCallback(async () => {
		const mine = ++generation.current;
		setLoaded(false);
		setError(false);

		const resp = await get(url).catch(() => null);
		if (mine !== generation.current) return;

		const bucket = resp?.data?.[0];
		if (!bucket) {
			/* Keep the last known counts. Blanking them on a failed request made
			   every badge vanish and come back, which reads as "the numbers are
			   broken" rather than "one request failed" — and the rows going empty
			   already says the load failed. */
			setRows([]);
			setError(true);
			setLoaded(true);
			return;
		}

		setRows(bucket.data ?? []);
		/* TOTALS, NOT UNVIEWED COUNTS.
		   These fed off `totalUnviewed*Count` — how many sessions in each tab had
		   never been opened. A tab badge's job is to predict what clicking the
		   tab shows, and those numbers answered a different question: the badge
		   said 135 where the list held 146, and it fell as drawers were opened.
		   Rejected, Warning and Assigned showed nothing at all — not because they
		   were empty (1, 3 and 1) but because everything in them had been viewed.
		   `tabCounts` is the per-tab total under the filters currently active.

		   `total` was the PAGE count, not a session count, so the header read
		   "83 active sessions" for 1232 of them and the pager offered 6 pages
		   instead of 83. `totalCount` is the real figure; `total` is left alone
		   because the sidebar badge and the old list both read it as pages. */
		const tc = bucket.tabCounts ?? {};
		setCounts({
			total: bucket.totalCount ?? 0,
			all: tc.all ?? 0,
			pending: tc.pending ?? 0,
			processing: tc.processing ?? 0,
			rejected: tc.rejected ?? 0,
			completed: tc.completed ?? 0,
			amlWarning: tc.amlWarning ?? 0,
			assigned: tc.assigned ?? 0,
		});
		setLoaded(true);
		// useNetwork's `get` identity churns every render; depending on it would
		// re-fire the effect below forever.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [url]);

	useEffect(() => {
		load();
	}, [load]);

	const pages = Math.max(1, Math.ceil((counts.total || 0) / PAGE_SIZE));

	return { rows, counts, pages, loaded, error, reload: load };
};

/** Keystrokes are not requests: hold the value until typing stops. */
export const useDebounced = <T,>(value: T, delay = 400) => {
	const [held, setHeld] = useState(value);
	useEffect(() => {
		const id = window.setTimeout(() => setHeld(value), delay);
		return () => window.clearTimeout(id);
	}, [value, delay]);
	return held;
};
